import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth, hasRole } from "@/context/AuthContext.jsx";
import { Card } from "@/components/ui/card.jsx";
import { Button } from "@/components/ui/button.jsx";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog.jsx";
import { Input } from "@/components/ui/input.jsx";
import ActionsDropdown from "@/components/ActionsDropdown.jsx";
import CommandPalette from "@/components/CommandPalette.jsx";
import MenuConfigDialog from "@/components/MenuConfigDialog.jsx";
import api from "@/api/client";
import { EyeOff, Eye } from "lucide-react";

/** Blickschutz: blendet alle Preise (Klasse .preis) per Blur aus. */
function PreiseToggle() {
  const [hidden, setHidden] = useState(() => localStorage.getItem("preiseAusblenden") === "1");

  React.useEffect(() => {
    document.documentElement.classList.toggle("preise-aus", hidden);
    localStorage.setItem("preiseAusblenden", hidden ? "1" : "0");
  }, [hidden]);

  return (
    <Button
      size="sm"
      variant={hidden ? "default" : "outline"}
      className={hidden ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}
      onClick={() => setHidden((v) => !v)}
      title={hidden ? "Preise wieder einblenden" : "Preise ausblenden (Blickschutz)"}
      aria-label={hidden ? "Preise einblenden" : "Preise ausblenden"}
      data-testid="toggle-preise-btn"
    >
      {hidden ? <EyeOff size={16} /> : <Eye size={16} />}
    </Button>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [userEditOpen, setUserEditOpen] = useState(false);
  const [menuConfigOpen, setMenuConfigOpen] = useState(false);

  // Keyboard shortcut for Command Palette
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <header className="sticky top-0 z-50 backdrop-blur bg-white/70 border-b">
        <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/assets/mps-icon-blue.png" alt="MPS Icon" className="h-10 w-10" />
              <img src="/assets/MPS_Header_Logo.png" alt="Moebus Productivity Suite" className="h-10 w-auto" />
            </Link>
            <nav className="hidden md:flex items-center gap-3 text-sm text-neutral-600">
              <NavLink to="/used" current={loc.pathname === "/used"}>Used</NavLink>
            </nav>
          </div>
          <div className="flex items-center gap-3">
            {user && (
              <Link to="/mobile-upload">
                <Button size="sm" variant="outline">📷 Mobile-APP</Button>
              </Link>
            )}
            {user && <ActionsDropdown />}
            {user && <PreiseToggle />}
            {user ? (
              <>
                <button 
                  onClick={() => setUserEditOpen(true)}
                  className="text-sm text-neutral-600 hidden sm:inline font-medium hover:text-blue-600 transition-colors cursor-pointer"
                >
                  {user.name || user.email}
                </button>
                <Button size="sm" variant="secondary" onClick={logout}>Logout</Button>
              </>
            ) : (
              <Link to="/login" className="text-sm text-neutral-600">Login</Link>
            )}
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl p-4 sm:p-6">
        <Card className="p-4 sm:p-6 bg-white/80 shadow-[0_10px_30px_-15px_rgba(0,0,0,0.2)]">
          {children}
        </Card>
      </main>

      {/* Command Palette */}
      {user && (
        <CommandPalette 
          open={commandPaletteOpen} 
          onClose={() => setCommandPaletteOpen(false)} 
        />
      )}

      {/* User Edit Dialog */}
      <UserEditDialog 
        open={userEditOpen}
        onClose={() => setUserEditOpen(false)}
        user={user}
        onOpenMenuConfig={() => {
          setUserEditOpen(false);
          setMenuConfigOpen(true);
        }}
      />

      {/* Menu Config Dialog */}
      {user && (hasRole(user, "superadmin") || hasRole(user, "backoffice")) && (
        <MenuConfigDialog
          open={menuConfigOpen}
          onClose={() => setMenuConfigOpen(false)}
          currentUser={user}
        />
      )}
    </div>
  );
}

// Benutzer-Bearbeitungs-Dialog
function UserEditDialog({ open, onClose, user, onOpenMenuConfig }) {
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  
  const canConfigureMenu = user && (hasRole(user, "superadmin") || hasRole(user, "backoffice"));

  React.useEffect(() => {
    if (user && open) {
      setDisplayName(user.name || user.email);
    }
  }, [user, open]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.patch("/users/me", { name: displayName });
      alert("Name erfolgreich aktualisiert! Bitte laden Sie die Seite neu.");
      onClose();
      window.location.reload(); // Reload um neuen Namen anzuzeigen
    } catch (err) {
      console.error("Fehler beim Speichern:", err);
      alert("Fehler beim Speichern des Namens");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Benutzer bearbeiten</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              E-Mail
            </label>
            <Input 
              value={user.email} 
              disabled 
              className="bg-gray-100"
            />
            <p className="text-xs text-gray-500 mt-1">E-Mail kann nicht geändert werden</p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Angezeigter Name
            </label>
            <Input 
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Ihr Name"
            />
          </div>

          {/* Menü-Verwaltung Button (nur für Superadmin/Backoffice) */}
          {canConfigureMenu && (
            <div className="pt-4 border-t">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={onOpenMenuConfig}
              >
                ⚙️ Menü-Verwaltung
              </Button>
              <p className="text-xs text-gray-500 mt-2">
                Passen Sie an, welche Menüpunkte im "Aktionen"-Menü angezeigt werden.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Speichert..." : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NavLink({ to, current, children }) {
  return (
    <Link to={to} className={`px-3 py-1.5 rounded-md ${current ? "bg-neutral-900 text-white" : "hover:bg-neutral-100"}`}>
      {children}
    </Link>
  );
}
